/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import CreateEventActionEventName from 'cio/app-models/create-event/event-name';
import {
  CreateEventEditorType,
  DataActionAutocompleteValuesContainer,
  DataActionComposerSectionType,
  DataActionConditionType,
  DataActionTriggerAttributeDropdownItem,
  getTopLevelKeyFromConditionType,
} from 'cio/utils/campaigns/data-action';
import EnvironmentService from 'cio/services/environment';
import { action } from '@ember/object';
import { Editor } from 'cio/services/editor';
import DataActionBodyAttributeRow from 'cio/app-models/data-action/body-attribute-row';
import { isEmpty } from '@ember/utils';
import hasLiquid from 'cio/utils/validators/liquid';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import { tracked } from '@glimmer/tracking';
import { CreateEventPreview } from 'cio/utils/previews';
import { prettyJSONStringify } from 'cio/utils/objects';
import DataActionRecipientCondition from 'cio/app-models/data-action/recipient-condition';
import { schedule } from '@ember/runloop';
import { buildWaiter } from '@ember/test-waiters';
import { ComposerCampaignActionTemplate } from 'cio/utils/composer/composer-editable-entity';
import RouterService from '@ember/routing/router-service';
import CampaignModel from 'cio/models/campaign';

interface CreateEventEditorEventArgs {
  editableEntity: ComposerCampaignActionTemplate;
  title: string;
  eventName: CreateEventActionEventName;
  defaultEditorRows: DataActionBodyAttributeRows;
  recipientCondition?: DataActionRecipientCondition;
  autocompleteValues: DataActionAutocompleteValuesContainer;
  isLoadingAsyncAutocompleteValues: boolean;
  supportedConditionTypes: DataActionConditionType[];
  updateHighlight: (highlight: DataActionTriggerAttributeDropdownItem | null) => unknown;
  liquidFieldFocusIn: (fieldName: string, index?: number) => unknown;
  liquidFieldFocusOut: () => unknown;
  switchEditor: (editor: CreateEventEditorType) => unknown;
  preview: CreateEventPreview;
  isPreviewLoading: boolean;
  trackPreviewErrors: (
    element: HTMLElement,
    [errors]: [{ field: string; label: string; errors: string[] }[]],
  ) => unknown;
  previewDidChange?: () => unknown;
  campaign: CampaignModel;
  source?: string;
  hasReadOnlyPermission: boolean;
}

let switchEditorWaiter = buildWaiter('create-event:switch-editor');

// We convert the rows to a JSON string manually, rather than using
// JSON.stringify, because JSON.stringify might try and escape quote characters
// within liquid expressions. We want liquid expression to remain as-is, so that
// they still get evaluated correctly.
function convertRowsToJSON(rows: DataActionBodyAttributeRow[]) {
  rows = rows.filter((row) => !(isEmpty(row.name) && isEmpty(row.value)));

  // We can't reliably convert JS to liquid expressions, so we drop them
  rows = rows.filter((row) => row.type !== DataActionConditionType.JavaScript);

  let maxIndex = rows.length - 1;

  let space = '  ';

  let innerJsonString = rows.reduce<string>((acc, row, index) => {
    let value = '';
    let topLevelKey = getTopLevelKeyFromConditionType(row.type);
    if (topLevelKey != null && !isEmpty(row.value)) {
      value = `{{${topLevelKey}.${row.value}}}`;
    } else {
      value = row.value;
    }
    acc = `${acc}\n${space}"${row.name}": "${value}"`;

    if (index !== maxIndex) {
      acc = `${acc},`;
    }

    return acc;
  }, '');

  return `{${innerJsonString}\n}`;
}

function convertRowsToJS(rows: DataActionBodyAttributeRow[]) {
  rows = rows.filter((row) => !(isEmpty(row.name) && isEmpty(row.value)));

  // We can't reliably convert liquid to JS expressions, so we drop them
  rows = rows.filter((row) => row.type !== DataActionConditionType.Liquid);

  let maxIndex = rows.length - 1;

  let space = '  ';

  let innerObjString = rows.reduce<string>((acc, row, index) => {
    let value = '';
    if (row.type === DataActionConditionType.JavaScript) {
      // For JavaScript rows, we'll prepare a value that is an immediately
      // invoked arrow function, whose contents are the value of the row. This
      // way we can maintain whatever JS the customer has typed in.
      acc = `${acc}\n${space}"${row.name}": (() => {
    ${row.value.replace(/\n/g, `\n${space}${space}`)}
  })()`;
    } else {
      let topLevelKey = getTopLevelKeyFromConditionType(row.type);
      if (topLevelKey != null && !isEmpty(row.value)) {
        value = `${topLevelKey}.${row.value}`;
      } else {
        value = `"${row.value}"`;
      }
      acc = `${acc}\n${space}"${row.name}": ${value}`;
    }

    if (index !== maxIndex) {
      acc = `${acc},`;
    }

    return acc;
  }, '');

  return `return {${innerObjString}
};`;
}

function convertJSONToJS(liquidString: string) {
  try {
    let data = JSON.parse(liquidString);
    let rows = Object.keys(data)
      .filter((key) => {
        let value = data[key];
        // Technically we can support arrays and objects, but what if array
        // items or object values have liquid? Let's drop it all for now
        return (typeof value === 'string' || typeof value === 'number') && !hasLiquid(String(value));
      })
      .map((key) => ({
        name: key,
        type: DataActionConditionType.Static,
        value: data[key],
      }));
    return convertRowsToJS(rows as DataActionBodyAttributeRow[]);
  } catch {
    return 'return { }';
  }
}

function convertJSONToRows(liquidString: string) {
  try {
    let data = JSON.parse(liquidString);
    return JSON.stringify(
      Object.keys(data)
        .filter((key) => {
          let value = data[key];
          return typeof value === 'string' || typeof value === 'number';
        })
        .map((key) => {
          return {
            name: key,
            type: hasLiquid(data[key]) ? DataActionConditionType.Liquid : DataActionConditionType.Static,
            value: data[key],
          };
        }),
    );
  } catch {
    return '';
  }
}

export default class CreateEventEditorEvent extends Component<CreateEventEditorEventArgs> {
  @service environment!: EnvironmentService;
  @service router!: RouterService;

  CreateEventEditorType = Editor;
  DataActionConditionType = DataActionConditionType;
  DataActionComposerSectionType = DataActionComposerSectionType;

  get previewString() {
    let data: Record<string | number, string | number | boolean | null> = {};

    try {
      let parsedData = JSON.parse(this.args.preview?.body);

      if (this.isJSONEditorActive) {
        data = parsedData;
      } else {
        // Make sure the preview object has keys in the same order as the rows
        this.args.defaultEditorRows.nonEmptyMappedRows.forEach((row) => {
          data[row.name] = parsedData[row.name] ?? '';
        });
      }
    } catch {}

    return prettyJSONStringify({
      name: this.args.preview?.name ?? '',
      data,
    });
  }

  @tracked showSwitchToDefaultEditorModal = false;
  @tracked switchToDefaultEditorHasInvalidJSON = false;
  @tracked showSwitchEditorModal = false;
  @tracked targetEditor: CreateEventEditorType | null = null;

  get isJSONEditorActive() {
    let { editor } = this.args.editableEntity.template;
    return editor === Editor.JSON || editor === Editor.JS;
  }

  @action
  switchEditor(editor: CreateEventEditorType) {
    if (editor === this.args.editableEntity.template.editor) {
      return;
    }

    this.targetEditor = editor;
    this.showSwitchEditorModal = true;
  }

  @action
  confirmSwitchEditor() {
    let { template } = this.args.editableEntity;
    let currentEditor = template.editor;
    let { targetEditor } = this;

    if (this.targetEditor == null) {
      return;
    }

    if (currentEditor === Editor.DEFAULT) {
      if (targetEditor === Editor.JS) {
        // When switching to from default to JS mode, convert the event rows to
        // regular key: value entries, and produce a JS expression to return
        // an object
        template.body = convertRowsToJS(this.args.defaultEditorRows.rows);
      } else if (targetEditor === Editor.JSON) {
        // When switching from default to JSON mode, convert the event rows to
        // regular key: value entries, and produce a JSON-like string
        template.body = convertRowsToJSON(this.args.defaultEditorRows.rows);
      }
    } else if (currentEditor === Editor.JS) {
      // When switching from JS to any mode, clear all template data because we
      // can't reliably determine the structure of the JS expression
      template.body = '';
    } else if (currentEditor === Editor.JSON) {
      if (targetEditor === Editor.JS) {
        // When switching from JSON to JS mode, try converting the liquid JSON
        // expression to a JS statement returning an object
        template.body = convertJSONToJS(template.body);
      } else if (targetEditor === Editor.DEFAULT) {
        // When switching from JSON to default mode, try converting the liquid
        // to rows, but:
        // - drop nested data
        // - drop arrays
        template.body = convertJSONToRows(template.body);
      }
    }

    // When switching between JS and JSON modes, we need to reset the editor to
    // default to destroy the Ace editor first. The Ace editor component can't
    // detect changes on the body and reinitialize, so we need to destroy and
    // rerender the whole thing
    let token = switchEditorWaiter.beginAsync();

    this.args.switchEditor(Editor.DEFAULT);
    schedule('afterRender', this, () => {
      if (targetEditor == null) {
        return;
      }

      this.args.switchEditor(targetEditor);
      switchEditorWaiter.endAsync(token);
    });
    this.closeSwitchEditorModal();
  }

  @action
  closeSwitchEditorModal() {
    this.showSwitchEditorModal = false;
    this.targetEditor = null;
  }

  get showCampaignTriggerLoopBanner() {
    let isEventOrFormTriggered = this.args.campaign?.isEventTriggered || this.args.campaign?.isFormTriggered;
    return (
      isEventOrFormTriggered &&
      this.args.campaign.event === this.args.eventName.value &&
      isEmpty(this.args.campaign.attributeFilters) &&
      this.args.campaign.restartMinInterval === 0
    );
  }

  get source() {
    return this.args.source ?? 'Send Event';
  }
}
